import { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import findPublicTalentsByBusinessLineRequest from '../../../core/api/requests/companies/talent/findPublicTalentsByBusinessLineRequest'
import ITalent from '../../../core/interfaces/ITalent'
import TalentBigButton from '../../../utils/TalentBigButton'
import ModalTalentDetails from './Modal/ModalTalentDetails'

const TalentsPublicView: FC = () => {
  const [talents, setTalents] = useState<ITalent[]>([])
  const [selectedTalent, setSelectedTalent] = useState<ITalent | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const dispatch = useDispatch()
  const params = useParams()

  useEffect(() => {
    const fetchTalents = async () => {
      if (!params.cid || !params.businessLine) return

      setLoading(true)
      const response = await findPublicTalentsByBusinessLineRequest(
        dispatch,
        params.cid,
        params.businessLine,
      )

      if (response.statusCode === 200 && response.data?.talents) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const talentArray = response.data.talents.map((t: Record<string, any>) => ({
          id: t.id,
          fullName: t.firstName,
          profileImage: t.profileImage,
          talent: {
            physical: t.physical,
            media: t.media,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          images: t.media?.files?.map((f: Record<string, any>) => f.url) || [],
        }))
        setTalents(talentArray)
      }
      setLoading(false)
    }

    fetchTalents()
  }, [params.cid, params.businessLine, dispatch])

  const downloadTalentPDF = (talentId: string) => {
    if (!params.cid || !params.businessLine) return
    const apiUrl = import.meta.env.VITE_APP_API_URL
    window.open(`${apiUrl}/p/company/${params.cid}/talents/${params.businessLine}/${talentId}/pdf`, '_blank')
  }

  const businessLineLabel = params.businessLine
    ? params.businessLine.charAt(0).toUpperCase() + params.businessLine.slice(1).toLowerCase()
    : ''

  return (
    <div className='app-wrapper flex-column flex-row-fluid' id='kt_app_wrapper'>
      <div className='app-main flex-column flex-row-fluid p-12' id='kt_app_main'>
        <div className="d-flex align-items-center justify-content-between mb-6">
          <img alt="Logo" src="/media/logos/default-small.svg" className="h-25px" />
          <span className="fw-semibold text-gray-700 fs-5">{businessLineLabel}</span>
        </div>

        <div className='d-flex flex-column flex-column-fluid'>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-20">
              <span className="spinner-border spinner-border-lg text-primary"></span>
            </div>
          ) : talents.length > 0 ? (
            <div className='row gx-1'>
              {talents.map((talent) => (
                <TalentBigButton
                  key={talent.id}
                  talent={talent}
                  showDetails={true}
                  allowSelect={false}
                  publicView={true}
                  onSelect={() => setSelectedTalent(talent)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center text-muted py-20 fs-5">
              No talents available
            </div>
          )}
        </div>
      </div>

      {!!selectedTalent && (
        <ModalTalentDetails
          show={!!selectedTalent}
          onClose={() => setSelectedTalent(null)}
          selectedTalent={selectedTalent}
          onDownloadPDF={() => selectedTalent?.id && downloadTalentPDF(selectedTalent.id)}
        />
      )}
    </div>
  )
}

export { TalentsPublicView }
